/**
 * Centralized wearable provider configuration.
 * Add new Terra providers here — the rest of the app picks them up automatically.
 */

import corosLogo from '@/assets/logos/coros-logo.png';

export interface WearableProvider {
    id: string;
    label: string;
    logo: string;
    logoAlt?: string; // alternate logo for dark mode
    logoStyle?: { borderRadius?: number; background?: string };
    connectionType: 'whoop' | 'terra';
    enabled: boolean;
}

export const WEARABLE_PROVIDERS: WearableProvider[] = [
    {
        id: 'whoop',
        label: 'WHOOP',
        logo: '/logos/whoop/Logo/Circle/WHOOP Circle Black.svg',
        logoAlt: '/logos/whoop/Logo/Circle/WHOOP Circle White.svg',
        logoStyle: { borderRadius: 10, background: '#000000' },
        connectionType: 'whoop',
        enabled: true,
    },
    {
        id: 'garmin',
        label: 'GARMIN',
        logo: '/logos/garmin/garmin-branding/Garmin_Connect_app_1024x1024-02.png',
        logoStyle: { borderRadius: 6 },
        connectionType: 'terra',
        enabled: true,
    },
    {
        id: 'coros',
        label: 'COROS',
        logo: corosLogo,
        logoStyle: { borderRadius: 6 },
        connectionType: 'terra',
        enabled: true,
    },
    {
        id: 'oura',
        label: 'OURA',
        logo: '/logos/oura/oura-logo.png',
        logoAlt: '/logos/oura/oura-logo.png',
        logoStyle: { borderRadius: 6 },
        connectionType: 'terra',
        enabled: true,
    },
];

/** All enabled provider IDs */
export const ENABLED_PROVIDER_IDS = WEARABLE_PROVIDERS.filter(p => p.enabled).map(p => p.id);

/** Terra provider IDs — these share the same backend data endpoint */
export const TERRA_PROVIDER_IDS = WEARABLE_PROVIDERS.filter(p => p.connectionType === 'terra' && p.enabled).map(p => p.id);

/** Check if an entity has any wearable connected */
export function isAnyWearableConnected(entity: {
    whoop_connected?: boolean;
    garmin_connected?: boolean;
    coros_connected?: boolean;
    oura_connected?: boolean;
    terra_connected?: { [provider: string]: boolean };
}): boolean {
    if (entity.whoop_connected) return true;
    if (entity.garmin_connected) return true;
    if (entity.coros_connected) return true;
    if (entity.oura_connected) return true;
    if (entity.terra_connected) {
        return Object.values(entity.terra_connected).some(v => v);
    }
    return false;
}

/** Get a human-readable label for the connected provider(s) */
export function getConnectedProviderLabel(entity: {
    whoop_connected?: boolean;
    garmin_connected?: boolean;
    coros_connected?: boolean;
    oura_connected?: boolean;
    data_source?: string;
}): string {
    if (entity.data_source) {
        const sources = entity.data_source.split('+');
        return sources.map(s => {
            const provider = WEARABLE_PROVIDERS.find(p => p.id === s.trim());
            return provider?.label || s.trim().toUpperCase();
        }).join(' + ') + ' Connected';
    }

    const connected: string[] = [];
    if (entity.whoop_connected) connected.push('WHOOP');
    if (entity.garmin_connected) connected.push('Garmin');
    if (entity.coros_connected) connected.push('COROS');
    if (entity.oura_connected) connected.push('Oura');

    if (connected.length === 0) return 'No Data Source';
    return connected.join(' + ') + ' Connected';
}

/** Return IDs of providers that are connected for the given entity */
export function getConnectedProviderIds(entity: {
    whoop_connected?: boolean;
    garmin_connected?: boolean;
    coros_connected?: boolean;
    oura_connected?: boolean;
    terra_connected?: { [provider: string]: boolean };
}): string[] {
    const connected: string[] = [];
    if (entity.whoop_connected) connected.push('whoop');
    if (entity.garmin_connected) connected.push('garmin');
    if (entity.coros_connected) connected.push('coros');
    if (entity.oura_connected) connected.push('oura');
    if (entity.terra_connected) {
        for (const [provider, isConnected] of Object.entries(entity.terra_connected)) {
            if (isConnected && !connected.includes(provider)) {
                connected.push(provider);
            }
        }
    }
    return connected;
}

/** Lookup a provider by id */
export function getProvider(id: string): WearableProvider | undefined {
    return WEARABLE_PROVIDERS.find(p => p.id === id);
}

/** Return a human-readable label for the currently selected Terra providers.
 *  Examples: "GARMIN", "COROS", "GARMIN + COROS" */
export function getTerraSourceLabel(selectedSources: string[]): string {
    const terraSelected = selectedSources.filter(s => TERRA_PROVIDER_IDS.includes(s));
    if (terraSelected.length === 0) return 'Wearable';
    return terraSelected.map(id => {
        const provider = WEARABLE_PROVIDERS.find(p => p.id === id);
        return provider?.label || id.toUpperCase();
    }).join(' + ');
}
